import { Search } from "lucide-react";

import type { Supplier } from "@/client";
import type { MovementDirection } from "@/domain/movements";

export type ClassificationFilter = "ALL" | "PENDING" | "CLASSIFIED";
export type TransactionFilterView = "MOVEMENTS" | "SUGAR" | "PACKAGING" | "FREIGHT";

interface SharedFilters {
	classification: ClassificationFilter;
	search: string;
	visibleCount: number;
	onClassification: (value: ClassificationFilter) => void;
	onSearch: (value: string) => void;
}

interface MovementFilters extends SharedFilters {
	view: "MOVEMENTS";
	direction: MovementDirection;
	onDirection: (value: MovementDirection) => void;
}

interface SupplierFilters extends SharedFilters {
	view: Exclude<TransactionFilterView, "MOVEMENTS">;
	supplierId: string;
	suppliers: Supplier[];
	onSupplier: (value: string) => void;
}

export function TransactionFilters(props: MovementFilters | SupplierFilters) {
	return (
		<div className="border-b border-stone-200 p-4 sm:p-5">
			<div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
				{props.view === "MOVEMENTS" ? (
					<div className="flex flex-wrap gap-1 rounded-xl bg-stone-100 p-1">
						<FilterButton
							active={props.direction === "DEBIT"}
							onClick={() => props.onDirection("DEBIT")}
						>
							Saídas
						</FilterButton>
						<FilterButton
							active={props.direction === "ALL"}
							onClick={() => props.onDirection("ALL")}
						>
							Todas
						</FilterButton>
						<FilterButton
							active={props.direction === "CREDIT"}
							onClick={() => props.onDirection("CREDIT")}
						>
							Entradas
						</FilterButton>
					</div>
				) : (
					<select
						aria-label={
							props.view === "SUGAR"
								? "Filtrar fornecedor de açúcar"
								: props.view === "PACKAGING"
									? "Filtrar fornecedor de embalagens"
									: "Filtrar transportadora de frete de entrada"
						}
						value={props.supplierId}
						onChange={(event) => props.onSupplier(event.target.value)}
						className="h-10 rounded-xl border border-stone-200 bg-white px-3 text-sm font-medium text-stone-700 outline-none transition focus:border-emerald-700 focus:ring-3 focus:ring-emerald-100"
					>
						<option value="ALL">
							{props.view === "FREIGHT" ? "Todas as transportadoras" : "Todos os fornecedores"}
						</option>
						{props.suppliers.map((supplier) => (
							<option key={supplier.id} value={supplier.id}>
								{supplier.name}
							</option>
						))}
					</select>
				)}
				<div className="relative min-w-0 flex-1 xl:max-w-xs">
					<Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-stone-400" />
					<input
						type="search"
						value={props.search}
						onChange={(event) => props.onSearch(event.target.value)}
						placeholder="Buscar descrição ou destino"
						className="h-10 w-full rounded-xl border border-stone-200 bg-white pr-3 pl-9 text-sm outline-none transition placeholder:text-stone-400 focus:border-emerald-700 focus:ring-3 focus:ring-emerald-100"
					/>
				</div>
			</div>
			<div className="mt-3 flex flex-wrap items-center justify-between gap-3">
				<div className="flex gap-2 text-xs">
					<StatusFilter
						active={props.classification === "ALL"}
						onClick={() => props.onClassification("ALL")}
					>
						Todas
					</StatusFilter>
					<StatusFilter
						active={props.classification === "PENDING"}
						onClick={() => props.onClassification("PENDING")}
					>
						Pendentes
					</StatusFilter>
					<StatusFilter
						active={props.classification === "CLASSIFIED"}
						onClick={() => props.onClassification("CLASSIFIED")}
					>
						Classificadas
					</StatusFilter>
				</div>
				<span className="text-xs text-stone-500">{props.visibleCount} na lista</span>
			</div>
		</div>
	);
}

function FilterButton({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${active ? "bg-white text-stone-950 shadow-sm" : "text-stone-500 hover:text-stone-800"}`}
		>
			{children}
		</button>
	);
}

function StatusFilter({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-full border px-3 py-1.5 font-medium transition ${active ? "border-emerald-800 bg-emerald-50 text-emerald-900" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
		>
			{children}
		</button>
	);
}
